<template lang="pug">
  div(:class="[$style.avMediaText, {'imageOnRight': desktopImageAlignedRight}]")
    GoLocalSpinner(v-if="dynamicContent && !headerCopy")
    template(v-if="!dynamicContent || headerCopy && blockToUse.innerHtml")
      div
        Block(
          :block="blockToUse.innerBlocks[1]"
          class="block lg:hidden"
        )
        .mt-ds-sm.mb-ds-md
          Block(
            :block="blockToUse.innerBlocks[0]"
          )
      div(:class="$style.mediaTextContainer")
        Block(
          v-for="(innerBlock, index) in blockToUse.innerBlocks.slice(1)"
          :class="{ 'hidden lg:block': index === 0 }"
          :key="`responsiveMediaText-${parentElementName}-${index}`"
          :id="`responsiveMediaText-${parentElementName}-${index}`"
          :block="innerBlock")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters } from "vuex"
import { findBlockByAttribute } from "~/utils/block"
import { ALIGN, RESPONSIVE_IMAGE_SET } from "~/constants/blocks"
import { processBlock } from "~/scripts/useful-functions"
import { GET_BOOKINGS_COPY, GET_POSTAL_CODE } from "~/store/booking-insights"

export default defineComponent({
  name: "MediaTextResponsive",

  props: {
    block: {
      required: true,
      type: Object as () => Block
    },
    dynamicContent: {
      type: Boolean
    },
    lazy: {
      default: true,
      type: Boolean
    }
  },

  data() {
    return {
      replacementData: {} as Dictionary,
      blockToUse: {} as Block
    }
  },

  computed: {
    parentElementName() {
      // Because MediaTextResponsive is used several times on one page, hydration errors can occur when for-loop keys are reused.
      // A nice solution for this and for editors it to give the parent Group block a name that's unique on that page.
      const groupNameAttribute: string = (
        this.block.attributes.find(attr => attr.name === "metadata") || {
          value: '{\\"name\\":\\"unnamed-group\\"}'
        }
      ).value
      return JSON.parse(groupNameAttribute).name.replace(/\s+/g, "")
    },
    desktopImageAlignedRight() {
      const SUPPORTED_BLOCKS = [ALIGN, RESPONSIVE_IMAGE_SET]

      return SUPPORTED_BLOCKS.some(className => {
        const matchingBlock = findBlockByAttribute(this.block, "className", className)
        return matchingBlock && matchingBlock.innerBlocks[0] && findBlockByAttribute(matchingBlock.innerBlocks[0], "align", "right")
      })
    },
    ...mapGetters("booking-insights", {
      postalCode: GET_POSTAL_CODE,
      headerCopy: GET_BOOKINGS_COPY
    })
  },

  watch: {
    headerCopy: {
      immediate: true,
      handler(newValue: AvBookingsCopy) {
        if (newValue && this.dynamicContent) {
          const {
            cheeky_title,
            average_home_move_cost,
            average_four_bed_home_move_cost,
            average_number_of_moves_by_agent,
            location,
            short_postcode
          } = newValue
          this.replacementData = {
            ...this.replacementData,
            cheeky_title,
            average_home_move_cost,
            average_four_bed_home_move_cost,
            average_number_of_moves_by_agent,
            location,
            short_postcode
          }
          this.replaceDynamicContent()
        }
      }
    }
  },

  created() {
    if (this.dynamicContent) return
    this.blockToUse = { ...this.block }
  },

  methods: {
    replaceDynamicContent() {
      this.$nextTick(() => {
        this.blockToUse = processBlock(this.block, this.replacementData)
      })
    }
  }
})
</script>

<style lang="css" module>
.avMediaText {
  @apply flex flex-col gap-ds-lg;

  @screen lg {
    @apply grid gap-x-ds-xxl items-center;
    grid-template-columns: 1fr 1fr;
    &:global(.imageOnRight) {
      .mediaTextContainer {
        order: -1;
      }
    }
  }
}

.mediaTextContainer {
  :global {
    .av-buttons {
      @apply mt-ds-lg;
    }
    h2.av-heading {
      @apply mb-ds-lg;
    }
    h3.av-heading,
    h4.av-heading,
    h5.av-heading,
    h6.av-heading {
      @apply text-2xl mb-ds-xxs;
    }
    p {
      @apply mb-ds-md text-rebrandSm;
    }
    p + .av-heading {
      @apply mt-ds-lg;
    }
    h4.av-heading + p {
      @apply text-black50;
    }
    ul {
      @apply list-disc pl-ds-md lg:pl-ds-ml ml-ds-sm lg:ml-0;

      li {
        @apply text-base sm:text-rebrandSm leading-normal mb-ds-ms;
      }
    }
  }

  :global(.highlight) {
    @apply text-blue-primary;
  }
}
</style>
